/**
 * Toast context provider and useToast hook for programmatic
 * toast creation across the application.
 *
 * Wrap the app in <ToastProvider> and call useToast() from any
 * child component to show toast notifications.
 *
 * @module shared/notifications/ToastProvider
 */
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Toast } from './Toast';
import type { ToastVariant } from './Toast';

interface ToastEntry {
  id: string;
  message: string;
  variant: ToastVariant;
  autoDismiss?: boolean;
  dismissAfterMs?: number;
  compact?: boolean;
}

interface ToastContextValue {
  showToast: (
    message: string,
    variant: ToastVariant,
    options?: { autoDismiss?: boolean; dismissAfterMs?: number; compact?: boolean },
  ) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const containerStyle: CSSProperties = {
  position: 'fixed',
  top: '1rem',
  right: '1rem',
  left: '1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  alignItems: 'flex-end',
  zIndex: 60,
  pointerEvents: 'none',
};

const toastWrapperStyle: CSSProperties = {
  pointerEvents: 'auto',
  width: '100%',
  maxWidth: '28rem',
};

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const counterRef = useRef(0);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (
      message: string,
      variant: ToastVariant,
      options?: { autoDismiss?: boolean; dismissAfterMs?: number; compact?: boolean },
    ) => {
      counterRef.current += 1;
      const id = `toast-${counterRef.current}`;
      setToasts((prev) => [
        ...prev,
        {
          id,
          message,
          variant,
          autoDismiss: options?.autoDismiss,
          dismissAfterMs: options?.dismissAfterMs,
          compact: options?.compact,
        },
      ]);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div style={containerStyle} data-testid="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} style={toastWrapperStyle}>
            <Toast
              message={toast.message}
              variant={toast.variant}
              autoDismiss={toast.autoDismiss}
              dismissAfterMs={toast.dismissAfterMs}
              compact={toast.compact}
              onDismiss={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
